import { useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useSpreadsheetApiCall } from './useSpreadsheetApiCall';

enum QueueOperationType {
  WRITE = 'write',
  ATOMIC = 'atomic'
}

export enum ValueInputOption {
  RAW = 'RAW',
  USER_ENTERED = 'USER_ENTERED'
}

interface QueueOperation {
  id: string;
  spreadsheetId: string;
  type: QueueOperationType;
  range?: string;
  values?: any[][];
  valueInputOption?: ValueInputOption;
  // For column-based operations
  sheetName?: string;
  columnName?: string;
  rowNumber?: number;
  // For atomic read-then-write operations
  atomicFunction?: (currentValue: string) => string;
}

interface ColumnCache {
  [spreadsheetId: string]: {
    [sheetName: string]: {
      headers: string[];
      lastUpdated: number;
    };
  };
}

interface SheetMetadataCache {
  [spreadsheetId: string]: {
    sheets: Array<{
      title: string;
      sheetId: number;
    }>;
    lastUpdated: number;
  };
}

const MAX_RETRIES = 2;
const RATE_LIMIT_RETRY_DELAY = 60000; // 60 seconds for 429 errors
const REGULAR_RETRY_DELAY = 2000; // 2 seconds for other errors
const COLUMN_CACHE_TTL = 5 * 60 * 1000; // 5 minutes


export function useSheetsWriteQueue() {
  const { makeApiCall } = useSpreadsheetApiCall();
  const tokenConfig = useSelector((state: RootState) => state.googleSheets.auth.tokenConfig);
  const tokenConfigRef = useRef(tokenConfig);

  const queueRef = useRef<QueueOperation[]>([]);
  const processingRef = useRef(false);
  const columnCacheRef = useRef<ColumnCache>({});
  const sheetMetadataCacheRef = useRef<SheetMetadataCache>({});
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Keep ref up to date
  tokenConfigRef.current = tokenConfig;

  // Helper to convert column index to letter
  const columnIndexToLetter = (index: number): string => {
    let result = '';
    let i = index;
    while (i >= 0) {
      result = String.fromCharCode(65 + (i % 26)) + result;
      i = Math.floor(i / 26) - 1;
    }
    return result;
  };

  // Helper to get column range from cache or fetch fresh headers
  const getColumnRange = async (spreadsheetId: string, sheetName: string, columnName: string, rowNumber: number): Promise<string | null> => {
    const now = Date.now();
    const cache = columnCacheRef.current[spreadsheetId]?.[sheetName];

    // Always try cached data first (even if expired - better than nothing)
    if (cache) {
      const colIndex = cache.headers.findIndex((h: string) => h?.trim() === columnName);
      if (colIndex !== -1) {
        const colLetter = columnIndexToLetter(colIndex);
        const cachedRange = `${sheetName}!${colLetter}${rowNumber}`;
        const cacheAge = now - cache.lastUpdated;

        // If cache is fresh, use it immediately
        if (cacheAge < COLUMN_CACHE_TTL) {
          return cachedRange;
        } else {
        }

        // Cache is stale but we have data - try to refresh in background
        if (!tokenConfigRef.current) {
          return cachedRange;
        }

        try {
          const headerResult = await makeApiCall({
            url: `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetName)}!1:1`,
            maxRetries: 1,
            tokenConfig: tokenConfigRef.current
          });

          if (headerResult.success) {
            const headers: string[] = headerResult.data.values?.[0] || [];
            columnCacheRef.current[spreadsheetId][sheetName] = {
              headers,
              lastUpdated: now
            };

            const newColIndex = headers.findIndex((h: string) => h?.trim() === columnName);
            if (newColIndex !== -1) {
              const newColLetter = columnIndexToLetter(newColIndex);
              const newRange = `${sheetName}!${newColLetter}${rowNumber}`;
              return newRange;
            } else {
            }
          }
        } catch (error) {
        }

        return cachedRange;
      }
    }

    // No cache data - must fetch fresh headers
    try {
      const headerResult = await makeApiCall({
        url: `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetName)}!1:1`,
        maxRetries: 1,
        tokenConfig: tokenConfigRef.current
      });

      if (headerResult.success) {
        const headers: string[] = headerResult.data.values?.[0] || [];

        if (!columnCacheRef.current[spreadsheetId]) {
          columnCacheRef.current[spreadsheetId] = {};
        }
        columnCacheRef.current[spreadsheetId][sheetName] = {
          headers,
          lastUpdated: now
        };

        const colIndex = headers.findIndex((h: string) => h?.trim() === columnName);
        if (colIndex !== -1) {
          const colLetter = columnIndexToLetter(colIndex);
          const range = `${sheetName}!${colLetter}${rowNumber}`;
          return range;
        } else {
        }
      } else {
      }
    } catch (error) {
    }

    return null;
  };

  // Helper to get sheet name with caching
  const getSheetName = async (spreadsheetId: string, sheetGid?: string): Promise<string> => {
    const now = Date.now();
    const cache = sheetMetadataCacheRef.current[spreadsheetId];

    // Check if cache is fresh
    if (cache && (now - cache.lastUpdated) < COLUMN_CACHE_TTL) {
      const targetSheet = sheetGid
        ? cache.sheets.find((s: any) => s.sheetId.toString() === sheetGid) || cache.sheets[0]
        : cache.sheets[0];

      if (targetSheet) {
        const cacheAge = now - cache.lastUpdated;
        return targetSheet.title;
      }
    }

    // Cache miss or expired - fetch fresh metadata
    try {
      const metadataResult = await makeApiCall({
        url: `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=spreadsheetId,sheets.properties`,
        maxRetries: 1,
        tokenConfig: tokenConfigRef.current
      });

      if (metadataResult.success) {
        const sheets = (metadataResult.data.sheets || []).map((sheet: any) => ({
          title: sheet.properties?.title || 'Sheet1',
          sheetId: sheet.properties?.sheetId || 0
        }));


        // Update cache
        sheetMetadataCacheRef.current[spreadsheetId] = {
          sheets,
          lastUpdated: now
        };

        const targetSheet = sheetGid
          ? sheets.find((s: any) => s.sheetId.toString() === sheetGid) || sheets[0]
          : sheets[0];

        if (targetSheet) {
          return targetSheet.title;
        }
      } else {
      }
    } catch (error) {
    }

    // Fallback to default
    return 'Sheet1';
  };

  // Self-contained queue processor
  const processQueue = async () => {
    if (processingRef.current) return;

    // Check if we have token config before processing
    if (!tokenConfigRef.current) {
      // Schedule next processing cycle to try again later
      const nextDelay = 1000;
      intervalRef.current = setTimeout(() => processQueue(), nextDelay);
      return;
    }

    processingRef.current = true;

    while (queueRef.current.length > 0) {
      const op = queueRef.current.shift()!;

      let success = false;
      let attemptCount = 0;

      // Keep trying this operation until success or max retries
      while (!success && attemptCount <= MAX_RETRIES) {
        attemptCount++;
        try {
          let result;

          if (op.type === QueueOperationType.ATOMIC) {
            // Resolve column-based range if needed
            let range = op.range;
            if (op.sheetName && op.columnName && op.rowNumber) {
              const resolvedRange = await getColumnRange(op.spreadsheetId, op.sheetName, op.columnName, op.rowNumber);
              if (resolvedRange) {
                range = resolvedRange;
              } else {
                success = true; // Don't retry column not found
                break;
              }
            }

            // Read current value, apply user function, then write
            const readResult = await makeApiCall({
              url: `https://sheets.googleapis.com/v4/spreadsheets/${op.spreadsheetId}/values/${encodeURIComponent(range!)}`,
              maxRetries: 1,
              tokenConfig: tokenConfigRef.current
            });

            if (!readResult.success) {
              result = readResult; // Pass through the read error (including 429s)
            } else {
              const currentValue = readResult.data?.values?.[0]?.[0] || '';
              const newValue = op.atomicFunction!(currentValue);

              result = await makeApiCall({
                url: `https://sheets.googleapis.com/v4/spreadsheets/${op.spreadsheetId}/values/${encodeURIComponent(range!)}?valueInputOption=${op.valueInputOption || ValueInputOption.RAW}`,
                method: 'PUT',
                body: { values: [[newValue]] },
                maxRetries: 1,
                tokenConfig: tokenConfigRef.current
              });
            }
          } else {
            // Resolve column-based range if needed
            let range = op.range;
            if (op.sheetName && op.columnName && op.rowNumber) {
              const resolvedRange = await getColumnRange(op.spreadsheetId, op.sheetName, op.columnName, op.rowNumber);
              if (resolvedRange) {
                range = resolvedRange;
              } else {
                success = true; // Don't retry column not found
                break;
              }
            }

            result = await makeApiCall({
              url: `https://sheets.googleapis.com/v4/spreadsheets/${op.spreadsheetId}/values/${encodeURIComponent(range!)}?valueInputOption=${op.valueInputOption || ValueInputOption.RAW}`,
              method: 'PUT',
              body: { values: op.values },
              maxRetries: 1,
              tokenConfig: tokenConfigRef.current
            });
          }

          if (result.success) {
            success = true;
          } else if (result.status === 429 || result.error?.includes('429')) {
            // Rate limit - wait and retry
            if (attemptCount <= MAX_RETRIES) {
              await new Promise(resolve => setTimeout(resolve, RATE_LIMIT_RETRY_DELAY));
            } else {
              success = true;
            }
          } else {
            // Other error - wait briefly and retry
            if (attemptCount <= MAX_RETRIES) {
              const backoffDelay = REGULAR_RETRY_DELAY * Math.pow(2, attemptCount - 1);
              await new Promise(resolve => setTimeout(resolve, backoffDelay));
            } else {
              success = true;
            }
          }
        } catch (error) {
          if (attemptCount <= MAX_RETRIES) {
            const backoffDelay = REGULAR_RETRY_DELAY * Math.pow(2, attemptCount - 1);
            await new Promise(resolve => setTimeout(resolve, backoffDelay));
          } else {
            success = true;
          }
        }
      }

      // Small delay between different operations
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    processingRef.current = false;

    // Schedule next processing cycle
    const nextDelay = queueRef.current.length > 0 ? 100 : 1000; // Quick if more items, slow if empty
    intervalRef.current = setTimeout(() => processQueue(), nextDelay);
  };

  // Start the self-contained processing loop when hook is created
  useEffect(() => {
    // Start the first processing cycle
    processQueue();

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // Simple queue functions that just add items
  const queueWrite = (
    spreadsheetId: string,
    range: string,
    values: any[][],
    options: {
      valueInputOption?: ValueInputOption;
    } = {}
  ) => {
    const operation = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      spreadsheetId,
      type: QueueOperationType.WRITE,
      range,
      values,
      valueInputOption: options.valueInputOption || ValueInputOption.RAW
    };

    queueRef.current.push(operation);

  };


  const queueAtomicUpdate = (
    spreadsheetId: string,
    range: string,
    updateFunction: (currentValue: string) => string,
    options: {
      valueInputOption?: ValueInputOption;
    } = {}
  ) => {
    const operation = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      spreadsheetId,
      type: QueueOperationType.ATOMIC,
      range,
      atomicFunction: updateFunction,
      valueInputOption: options.valueInputOption || ValueInputOption.RAW
    };

    queueRef.current.push(operation);

  };

  const queueWriteByColumn = (
    spreadsheetId: string,
    sheetName: string,
    columnName: string,
    rowNumber: number,
    value: string,
    options: {
      valueInputOption?: ValueInputOption;
    } = {}
  ) => {
    const operation = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      spreadsheetId,
      type: QueueOperationType.WRITE,
      sheetName,
      columnName,
      rowNumber,
      values: [[value]],
      valueInputOption: options.valueInputOption || ValueInputOption.RAW
    };

    queueRef.current.push(operation);

  };

  const queueAtomicUpdateByColumn = (
    spreadsheetId: string,
    sheetName: string,
    columnName: string,
    rowNumber: number,
    updateFunction: (currentValue: string) => string,
    options: {
      valueInputOption?: ValueInputOption;
    } = {}
  ) => {
    const operation = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      spreadsheetId,
      type: QueueOperationType.ATOMIC,
      sheetName,
      columnName,
      rowNumber,
      atomicFunction: updateFunction,
      valueInputOption: options.valueInputOption || ValueInputOption.RAW
    };

    queueRef.current.push(operation);
  };

  // Function to invalidate column cache when structure changes
  const invalidateColumnCache = (spreadsheetId: string, sheetName: string) => {
    if (columnCacheRef.current[spreadsheetId]?.[sheetName]) {
      delete columnCacheRef.current[spreadsheetId][sheetName];
    }
  };

  return {
    queueWrite,
    queueAtomicUpdate,
    queueWriteByColumn,
    queueAtomicUpdateByColumn,
    getSheetName,
    invalidateColumnCache
  };
}